import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Divider,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useSelector } from 'react-redux';

/**
 * Dialog component for adding events to test case using actual values from test results
 *
 * For inbound events: Always creates new event at end of sequence
 * For outbound events: User can choose to add to existing event or create new one
 */
function AddExtraEventDialog({ open, onClose, event, onConfirm }) {
  const [selectedOption, setSelectedOption] = useState('new'); // 'new' or 'existing'
  const [selectedEventId, setSelectedEventId] = useState('');
  const [selectedScenarioId, setSelectedScenarioId] = useState('');
  const [selectedAttributes, setSelectedAttributes] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [duplicateDecisions, setDuplicateDecisions] = useState({}); // { attrName: 'skip' | 'overwrite' }
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);

  // Get current flow data from Redux
  const validationMetadata = useSelector((state) => state.validationResults.metadata);
  const flowsById = useSelector((state) => state.getFlows.flowsById);
  const currentFlow = flowsById[validationMetadata?.flow_db_id];

  const isInbound = event?.beaconType === 'inbound';
  const attributes = Object.entries(event?.beaconData?.data || {}).filter(
    ([key]) =>
      ![
        'event_name',
        'event_direction',
        'timestamp',
        'device_id',
        'is_blocked_event',
        'blocked_user_attributes',
        'market_id',
        'market_name',
      ].includes(key),
  );

  // Initialize all attributes as selected when dialog opens
  useEffect(() => {
    if (open && attributes.length > 0) {
      const initialSelection = {};
      attributes.forEach(([name]) => {
        initialSelection[name] = true;
      });
      setSelectedAttributes(initialSelection);
    }
  }, [open, attributes.length]);

  // Get scenarios matching the beacon type from the selected event (outbound only)
  const getMatchingScenarios = () => {
    if (!selectedEventId || isInbound || !event) return [];

    const selectedEvent = currentFlow?.events?.find((evt) => evt.event_db_id === selectedEventId);

    if (!selectedEvent) return [];

    const targetScenarioType = `outbound-${event.beaconType.toLowerCase()}`;

    return (
      selectedEvent.scenarios?.filter(
        (scenario) => scenario.scenario_type === targetScenarioType,
      ) || []
    );
  };

  const matchingScenarios = getMatchingScenarios();

  // Get existing attributes from the selected scenario
  const getExistingAttributes = () => {
    if (!selectedScenarioId) return [];

    const selectedEvent = currentFlow?.events?.find((evt) => evt.event_db_id === selectedEventId);

    const selectedScenario = selectedEvent?.scenarios?.find(
      (s) => s.scenario_db_id === selectedScenarioId,
    );

    return selectedScenario?.scenario_attributes || [];
  };

  // Find duplicates between selected attributes and existing scenario attributes
  // Only includes duplicates where the value is DIFFERENT (same value = no action needed)
  const findDuplicates = () => {
    const existing = getExistingAttributes();
    const existingNames = new Set(existing.map((a) => a.attribute_name));

    return (
      attributes
        .filter(([name]) => selectedAttributes[name] && existingNames.has(name))
        .map(([name, value]) => {
          const existingAttr = existing.find((a) => a.attribute_name === name);
          return {
            name,
            newValue: value,
            existingValue: existingAttr?.expected_value,
            existingAttrId: existingAttr?.attribute_db_id,
          };
        })
        // Filter out duplicates where the value is the same - no action needed
        .filter((dup) => String(dup.newValue) !== String(dup.existingValue))
    );
  };

  const duplicates = findDuplicates();

  if (!event) return null;

  const handleConfirm = async () => {
    // If existing scenario selected and we haven't shown duplicate warning yet, check for duplicates first
    if (selectedScenarioId && !showDuplicateWarning) {
      const dups = findDuplicates();
      if (dups.length > 0) {
        // Initialize all duplicates to 'skip' by default
        const initialDecisions = {};
        dups.forEach((d) => {
          initialDecisions[d.name] = 'skip';
        });
        setDuplicateDecisions(initialDecisions);
        setShowDuplicateWarning(true);
        return; // Don't submit yet, show warning first
      }
    }

    setLoading(true);
    setError(null);

    try {
      // Get all existing attribute names (for filtering same-value duplicates)
      const existingAttrs = getExistingAttributes();
      const existingAttrMap = new Map(
        existingAttrs.map((a) => [a.attribute_name, a.expected_value]),
      );

      // Attributes to add:
      // - Not in existing scenario at all, OR
      // - In existing with DIFFERENT value and user chose "overwrite"
      // Exclude: attributes that exist with the SAME value (no need to add)
      const attributesToAdd = attributes
        .filter(([name]) => selectedAttributes[name])
        .filter(([name, value]) => {
          // If not in existing scenario, include it
          if (!existingAttrMap.has(name)) return true;

          // If in existing with same value, skip it (no need to add)
          if (String(existingAttrMap.get(name)) === String(value)) return false;

          // If in existing with different value (it's in duplicates), only include if overwriting
          return duplicateDecisions[name] === 'overwrite';
        })
        .map(([name, value]) => ({ name, value }));

      // Attributes to delete (overwrites only)
      const attributesToDelete = duplicates
        .filter((d) => duplicateDecisions[d.name] === 'overwrite')
        .map((d) => ({
          id: d.existingAttrId,
          isInbound: isInbound,
        }));

      // Check if there's anything to add after filtering
      if (attributesToAdd.length === 0 && attributesToDelete.length === 0) {
        setError(
          'No new attributes to add. Selected attributes either already exist with the same value, or were skipped.',
        );
        setLoading(false);
        return;
      }

      const options = {
        isInbound,
        createNewEvent: isInbound || selectedOption === 'new',
        existingEventId: !isInbound && selectedOption === 'existing' ? selectedEventId : null,
        existingScenarioId:
          !isInbound && selectedOption === 'existing' && selectedScenarioId
            ? selectedScenarioId
            : null,
        eventName: event.eventName,
        beaconType: event.beaconType,
        attributes: attributesToAdd,
        attributesToDelete: attributesToDelete,
      };

      await onConfirm(options);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to add event to test case');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError(null);
      setSelectedOption('new');
      setSelectedEventId('');
      setSelectedScenarioId('');
      setSelectedAttributes({});
      setDuplicateDecisions({});
      setShowDuplicateWarning(false);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          fontWeight: 'bold',
        }}
      >
        Add Event to Test Case
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Event Info */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
            Event Details
          </Typography>
          <Box
            sx={{
              bgcolor: 'custom.surfaceAlt',
              p: 2,
              borderRadius: 1,
              display: 'flex',
              gap: 2,
              flexDirection: 'column',
            }}
          >
            <Box>
              <Typography variant="body2" color="text.secondary">
                Event Name
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {event.eventName}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Event Type
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 'bold',
                  textTransform: 'capitalize',
                  color: isInbound ? 'success.light' : 'text.secondary',
                }}
              >
                {isInbound ? '↓ Inbound' : `↗ ${event.beaconType}`}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Destination Options - collapsed when duplicate warning shown */}
        {showDuplicateWarning ? (
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              bgcolor: 'custom.surfaceAlt',
              borderRadius: 1,
              cursor: 'pointer',
              '&:hover': { bgcolor: 'action.hover' },
            }}
            onClick={() => {
              setShowDuplicateWarning(false);
              setDuplicateDecisions({});
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Destination:</strong>{' '}
                {selectedOption === 'new' ? (
                  'New event'
                ) : (
                  <>
                    {currentFlow?.events?.find((e) => e.event_db_id === selectedEventId)
                      ?.event_name || 'Existing event'}
                    {selectedScenarioId && (
                      <>
                        {' '}
                        →{' '}
                        {matchingScenarios.find((s) => s.scenario_db_id === selectedScenarioId)
                          ?.scenario_name || 'Existing scenario'}
                      </>
                    )}
                  </>
                )}
              </Typography>
              <Typography variant="caption" color="primary" sx={{ ml: 1 }}>
                Click to edit
              </Typography>
            </Box>
          </Box>
        ) : isInbound ? (
          <Box sx={{ mb: 3 }}>
            <Alert severity="info">
              This inbound event will be added as a new event at the end of the test case sequence.
            </Alert>
          </Box>
        ) : (
          <Box sx={{ mb: 3 }}>
            <FormControl component="fieldset">
              <FormLabel component="legend" sx={{ fontWeight: 'bold', mb: 1 }}>
                Where should this event be added?
              </FormLabel>
              <RadioGroup
                value={selectedOption}
                onChange={(e) => setSelectedOption(e.target.value)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Radio value="new" />
                  <Box>
                    <Typography variant="body1">Create new event</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Add as a new event at the end of the sequence
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Radio value="existing" />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body1">Add to existing event</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Add as a scenario under an existing event
                    </Typography>
                    {selectedOption === 'existing' && (
                      <>
                        <Select
                          fullWidth
                          value={selectedEventId}
                          onChange={(e) => {
                            setSelectedEventId(e.target.value);
                            setSelectedScenarioId(''); // Reset scenario when event changes
                            // Reset duplicate warning when event changes
                            setShowDuplicateWarning(false);
                            setDuplicateDecisions({});
                          }}
                          sx={{ mt: 1 }}
                          size="small"
                          displayEmpty
                        >
                          <MenuItem value="" disabled>
                            Select an event...
                          </MenuItem>
                          {currentFlow?.events?.map((evt) => (
                            <MenuItem key={evt.event_db_id} value={evt.event_db_id}>
                              Event {evt.event_order}: {evt.event_name}
                            </MenuItem>
                          ))}
                        </Select>

                        {/* Scenario dropdown - shown when event is selected */}
                        {selectedEventId && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              Add to scenario:
                            </Typography>
                            <Select
                              fullWidth
                              value={selectedScenarioId}
                              onChange={(e) => {
                                setSelectedScenarioId(e.target.value);
                                // Reset duplicate warning when scenario changes
                                setShowDuplicateWarning(false);
                                setDuplicateDecisions({});
                              }}
                              size="small"
                              displayEmpty
                            >
                              <MenuItem value="">
                                <em>Create new scenario</em>
                              </MenuItem>
                              {matchingScenarios.map((scenario) => (
                                <MenuItem
                                  key={scenario.scenario_db_id}
                                  value={scenario.scenario_db_id}
                                >
                                  {scenario.scenario_name}
                                </MenuItem>
                              ))}
                            </Select>
                            {matchingScenarios.length === 0 && (
                              <Alert severity="info" sx={{ mt: 1 }}>
                                No {event.beaconType} scenarios in this event. A new scenario will
                                be created.
                              </Alert>
                            )}
                          </Box>
                        )}
                      </>
                    )}
                  </Box>
                </Box>
              </RadioGroup>
            </FormControl>
          </Box>
        )}

        {!showDuplicateWarning && <Divider sx={{ my: 2 }} />}

        {/* Attributes Selection - collapsed when duplicate warning shown */}
        {showDuplicateWarning ? (
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              bgcolor: 'custom.surfaceAlt',
              borderRadius: 1,
              cursor: 'pointer',
              '&:hover': { bgcolor: 'action.hover' },
            }}
            onClick={() => {
              setShowDuplicateWarning(false);
              setDuplicateDecisions({});
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Selected attributes:</strong>{' '}
                {Object.values(selectedAttributes).filter(Boolean).length} of {attributes.length}
              </Typography>
              <Typography variant="caption" color="primary" sx={{ ml: 1 }}>
                Click to edit
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}
            >
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Attributes to be Added ({Object.values(selectedAttributes).filter(Boolean).length} /{' '}
                {attributes.length} selected)
              </Typography>
              <Box>
                <Button
                  size="small"
                  onClick={() => {
                    const allSelected = {};
                    attributes.forEach(([name]) => {
                      allSelected[name] = true;
                    });
                    setSelectedAttributes(allSelected);
                  }}
                  sx={{ mr: 1 }}
                >
                  Select All
                </Button>
                <Button
                  size="small"
                  onClick={() => {
                    const noneSelected = {};
                    attributes.forEach(([name]) => {
                      noneSelected[name] = false;
                    });
                    setSelectedAttributes(noneSelected);
                  }}
                >
                  Deselect All
                </Button>
              </Box>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Selected attributes will be copied with their actual values as expected values (Static
              type)
            </Typography>
            <Box
              sx={{
                maxHeight: '250px',
                overflow: 'auto',
                border: (theme) => `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
              }}
            >
              {attributes.map(([name, value], index) => (
                <Box
                  key={index}
                  sx={{
                    p: 1,
                    borderBottom: (theme) =>
                      index < attributes.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                    '&:nth-of-type(even)': { bgcolor: 'custom.surfaceAlt' },
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                  }}
                >
                  <Checkbox
                    checked={selectedAttributes[name] || false}
                    onChange={(e) => {
                      setSelectedAttributes((prev) => ({
                        ...prev,
                        [name]: e.target.checked,
                      }));
                    }}
                    size="small"
                    sx={{ pt: 0 }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                      {name}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        wordBreak: 'break-word',
                      }}
                    >
                      {value === null ? 'null' : String(value)}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Duplicate Warning Section */}
        {showDuplicateWarning && duplicates.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                {duplicates.length} attribute(s) already exist in this scenario. Choose how to
                handle each:
              </Alert>
              {/* Show warning if nothing will be added */}
              {(() => {
                const duplicateNames = new Set(duplicates.map((d) => d.name));
                const nonDuplicateCount = attributes.filter(
                  ([name]) => selectedAttributes[name] && !duplicateNames.has(name),
                ).length;
                const overwriteCount = duplicates.filter(
                  (d) => duplicateDecisions[d.name] === 'overwrite',
                ).length;
                const totalToAdd = nonDuplicateCount + overwriteCount;

                if (totalToAdd === 0) {
                  return (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      All selected attributes are duplicates and set to "Skip". Nothing will be
                      added. Select "Overwrite" for at least one attribute, or go back and select
                      different attributes.
                    </Alert>
                  );
                }
                return null;
              })()}
              <Box
                sx={{
                  maxHeight: '350px',
                  overflow: 'auto',
                  border: (theme) => `1px solid ${theme.palette.warning.main}`,
                  borderRadius: 1,
                }}
              >
                {duplicates.map((dup, index) => (
                  <Box
                    key={dup.name}
                    sx={{
                      p: 2,
                      borderBottom: (theme) =>
                        index < duplicates.length - 1
                          ? `1px solid ${theme.palette.divider}`
                          : 'none',
                      '&:nth-of-type(even)': {
                        bgcolor: (theme) => alpha(theme.palette.warning.main, 0.12),
                      },
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {dup.name}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Existing value:
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ wordBreak: 'break-word', color: 'text.secondary' }}
                        >
                          {dup.existingValue === null ? 'null' : String(dup.existingValue)}
                        </Typography>
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" color="primary">
                          New value:
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ wordBreak: 'break-word', color: 'info.main' }}
                        >
                          {dup.newValue === null ? 'null' : String(dup.newValue)}
                        </Typography>
                      </Box>
                    </Box>
                    <RadioGroup
                      row
                      value={duplicateDecisions[dup.name] || 'skip'}
                      onChange={(e) =>
                        setDuplicateDecisions((prev) => ({
                          ...prev,
                          [dup.name]: e.target.value,
                        }))
                      }
                    >
                      <FormControlLabel
                        value="skip"
                        control={<Radio size="small" />}
                        label="Skip (keep existing)"
                      />
                      <FormControlLabel
                        value="overwrite"
                        control={<Radio size="small" />}
                        label="Overwrite"
                      />
                    </RadioGroup>
                  </Box>
                ))}
              </Box>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          sx={{
            color: 'text.secondary',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={(() => {
            if (loading) return true;
            if (!isInbound && selectedOption === 'existing' && !selectedEventId) return true;
            if (Object.values(selectedAttributes).filter(Boolean).length === 0) return true;

            // If showing duplicate warning, check if anything will actually be added
            if (showDuplicateWarning) {
              const duplicateNames = new Set(duplicates.map((d) => d.name));
              const nonDuplicateCount = attributes.filter(
                ([name]) => selectedAttributes[name] && !duplicateNames.has(name),
              ).length;
              const overwriteCount = duplicates.filter(
                (d) => duplicateDecisions[d.name] === 'overwrite',
              ).length;
              if (nonDuplicateCount + overwriteCount === 0) return true;
            }

            return false;
          })()}
          variant="contained"
          sx={{
            bgcolor: 'primary.main',
            color: 'white',
            fontWeight: 'bold',
            textTransform: 'none',
            '&:hover': { bgcolor: 'primary.dark' },
            '&:disabled': { bgcolor: 'action.disabledBackground' },
          }}
          startIcon={loading && <CircularProgress size={16} color="inherit" />}
        >
          {loading ? 'Adding...' : showDuplicateWarning ? 'Confirm' : 'Add to Test Case'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AddExtraEventDialog;
